import { ROUTES } from "@/constants/routes";
import { withSidebarContext } from "@/context/SidebarContext";
import type { LocationState } from "@/types/pane-routing";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import { useUsageLimitReason } from "@/api/user";
import { useDisplayMode } from "@/hooks/useAppBadge";
import { useBreakpoint } from "@/hooks/useBreakpoint";

import { usePaneLayoutWidths } from "@/context/PaneLayoutWidthsContext";
import {
  RightPaneExpandContext,
  type RightPaneExpandContextValue,
} from "@/context/RightPaneExpandContext";
import { WorkspaceFloatingChatProvider } from "@/context/WorkspaceFloatingChatContext";
import { OsFileDropLayer } from "@/dnd/OsFileDropLayer";
import { CanvasClipboardProvider } from "@/pages/canvas/context/CanvasClipboardContext";
import { CanvasCommentsMirrorProvider } from "@/pages/canvas/context/CanvasCommentsMirrorContext";
import { CanvasNodesMirrorProvider } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import { CanvasSelectionMirrorProvider } from "@/pages/canvas/context/CanvasSelectionMirrorContext";
import { cn } from "@/utils";

import { AnalyticsShellContextSync } from "@/analytics/AnalyticsShellContextSync";
import { UsageLimitBanner } from "@/components/billing/UsageLimitBanner";
import { FileConflictResolverProvider } from "@/components/FileConflictResolver";
import { StatusBanner } from "@/components/status/StatusBanner";
import { WorkspaceFloatingChatPanel } from "@/components/WorkspaceFloatingChat/WorkspaceFloatingChatPanel";
import { WorkspaceAppHeader } from "@/layouts/WorkspaceLayout/components/WorkspaceAppHeader/WorkspaceAppHeader";
import {
  WorkspaceLeftRail,
  useWorkspaceLeftRailLayoutWidth,
  useWorkspaceNavZoneWidth,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceLeftRail";
import { WorkspaceMainRegion } from "@/layouts/WorkspaceLayout/components/WorkspaceMainRegion";
import { WorkspaceRailSearchHotkey } from "@/layouts/WorkspaceLayout/components/WorkspaceRailSearch/WorkspaceRailSearch";
import {
  WorkspaceDesktopTabBar,
  WorkspaceHeaderUtilities,
  WorkspaceTabBarSync,
  useWorkspaceTabBarState,
} from "@/layouts/WorkspaceLayout/components/WorkspaceTabBar/WorkspaceTabBar";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { useMaxPaneWidth } from "@cohesive/ui-components";
import { useFlags } from "launchdarkly-react-client-sdk";
import { CanvasDndConditional } from "../shared/CanvasDndConditional";
import { LayoutSettingsShell } from "../shared/LayoutSettingsShell";
import { RightPaneRouter } from "../shared/RightPaneRouter";

interface WorkspaceLayoutProps {
  children: React.ReactNode;
}

function WorkspaceLayoutHeader() {
  const lg = useBreakpoint("lg");
  const isHome = useMatch(ROUTES.HOME);
  const isCanvasRoot = useMatch(ROUTES.CANVAS.ROOT);
  const isWorkspaceRoot = useMatch(ROUTES.WORKSPACES.ROOT + "/*");
  const isSharedWithMe = useMatch(ROUTES.SHARED.ROOT);
  const isTrash = useMatch(ROUTES.ARCHIVE.ROOT);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);
  const isCanvasNew = useMatch(ROUTES.CANVAS.NEW);

  const showTabBar =
    !!isHome ||
    !!isWorkspaceRoot ||
    !!isCanvasRoot ||
    !!isSharedWithMe ||
    !!isTrash ||
    !!isChatDetail ||
    !!isCanvasDetail ||
    !!isCanvasNew;

  const { closeTabAndNavigate } = useWorkspaceTabBarState();

  const handleCloseTab = (id: string) => {
    closeTabAndNavigate(id);
  };

  return (
    <WorkspaceAppHeader
      showTabBar={showTabBar}
      tabBar={lg ? <WorkspaceDesktopTabBar /> : null}
      utilities={<WorkspaceHeaderUtilities onCloseTab={handleCloseTab} />}
    />
  );
}

const WorkspaceLayout: React.FC<WorkspaceLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const mainRef = useRef<HTMLDivElement>(null);

  const flags = useFlags<Flags>();
  const { reason } = useUsageLimitReason();

  const isCanvasDetail = !!useMatch(ROUTES.CANVAS.DETAIL);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);

  const isRightPanelOpen = !!location.state?.rightPane?.pathname;
  const navZoneWidth = useWorkspaceNavZoneWidth();
  const leftRailWidth = useWorkspaceLeftRailLayoutWidth();
  const maxPaneWidth = useMaxPaneWidth({
    isLeftPanelOpen: false,
    isRightPanelOpen,
  });

  const displayMode = useDisplayMode();

  const isFileRecordDetail = useMatch(ROUTES.FILE_RECORDS.DETAIL);
  const isCanvasIndex = useMatch(ROUTES.CANVAS.ROOT);
  const isWorkspaceDetail = useMatch(ROUTES.WORKSPACES.DETAIL);
  const isSharedWithMe = useMatch(ROUTES.SHARED.ROOT);
  const isHome = useMatch(ROUTES.HOME);
  const isSkillDetail = useMatch(ROUTES.SKILLS.SHOW);
  const isSkillRoot = useMatch(ROUTES.SKILLS.ROOT);
  const isTrash = useMatch(ROUTES.ARCHIVE.ROOT);

  const isDropZoneEnabled = useMemo(() => {
    return (
      !isSkillDetail &&
      !isSkillRoot &&
      !isHome &&
      !isSharedWithMe &&
      // The workspace detail page runs its own OS-file upload zone
      // (WorkspaceDropZone) — the layout layer's "add to conversation"
      // overlay must not fight it.
      !isWorkspaceDetail
    );
  }, [isSkillDetail, isSkillRoot, isHome, isSharedWithMe, isWorkspaceDetail]);

  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(false);
  const [leftChatDockPx, setLeftChatDockPx] = useState(0);
  const { rightPanelWidth, setRightPanelWidth } = usePaneLayoutWidths();

  const rightPaneExpandContextValue: RightPaneExpandContextValue = useMemo(
    () => ({
      isExpanded: isRightPanelExpanded,
      rightPanelWidth: isRightPanelOpen ? rightPanelWidth : 0,
      leftPanelWidth: 0,
      leftChatDockPx,
      setLeftChatDockPx,
      expandToMax: () => {
        setIsRightPanelExpanded(true);
      },
      // Collapsing just drops the expanded render override; the user's stored
      // (dragged) width comes back on its own.
      collapseToDefault: () => {
        setIsRightPanelExpanded(false);
      },
    }),
    [isRightPanelExpanded, isRightPanelOpen, rightPanelWidth, leftChatDockPx],
  );

  // Shared-doc deep links land with a one-shot `rightPaneExpanded` hint. Apply
  // it once the pane is open, then strip the flag so a later manual collapse
  // sticks and ordinary pane opens don't get force-expanded.
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (!state?.rightPaneExpanded || !isRightPanelOpen) return;

    setIsRightPanelExpanded(true);

    const rest = { ...state };
    delete rest.rightPaneExpanded;
    navigate(location.pathname + location.search, {
      replace: true,
      state: rest,
    });
  }, [location, isRightPanelOpen, navigate]);

  // Home and the workspace page are ONE surface (`HomeSurface`) at two
  // scopes — unscoped and force-filtered — so they stand on the same ground:
  // the shell's n-05 plus the DS line grid, with <main> transparent over it.
  // Anything that reads as "the home surface" belongs in here; the divergence
  // this replaced made the two scopes look like different products.
  const isGroundedSurface = !!isHome || !!isWorkspaceDetail;

  const isFullViewport =
    isFileRecordDetail ||
    isCanvasDetail ||
    isCanvasIndex ||
    isWorkspaceDetail ||
    isSharedWithMe ||
    isChatDetail ||
    isTrash ||
    isHome;

  const rightPaneMaxWidth = useMemo(() => {
    // useMaxPaneWidth reserves 80px for collapsed nav; subtract extra rail width.
    const hookReservedNavWidth = 80;
    const railExtraWidth = Math.max(0, navZoneWidth - hookReservedNavWidth);
    // One ceiling for every route: a detail pane outside a canvas used to
    // stop at 800 while the same pane on a canvas ran to the hook's max.
    return Math.max(400, maxPaneWidth.rightPane - railExtraWidth);
  }, [maxPaneWidth.rightPane, navZoneWidth]);

  return (
    <LayoutSettingsShell>
      <FileConflictResolverProvider>
        <>
          <WorkspaceTabBarSync />
          <AnalyticsShellContextSync />

          {displayMode === "standalone" && (
            <div className="border-t h-[1px] w-dvw absolute top-0 left-0 z-50"></div>
          )}

          <div className="flex h-dvh flex-col bg-n-05">
            <StatusBanner className="m-0 z-50" />
            {reason !== "none" && flags.billing && (
              <UsageLimitBanner className="m-0 z-50" />
            )}

            {/* pt-safe wrapper (not on the header itself: its h-11 is
                box-border, so padding there would crush the control row).
                Matches the header background so the notch / Dynamic Island
                strip is a seamless extension of the tab bar. Resolves to 0
                off-device. */}
            <div className="shrink-0 bg-surface-tab-bar pt-safe">
              <WorkspaceLayoutHeader />
            </div>

            <RightPaneExpandContext.Provider
              value={rightPaneExpandContextValue}
            >
              <WorkspaceFloatingChatProvider>
                <CanvasClipboardProvider>
                  <CanvasNodesMirrorProvider>
                    <CanvasCommentsMirrorProvider>
                      <CanvasSelectionMirrorProvider>
                        <CanvasDndConditional enabled>
                          <WorkspaceRailSearchHotkey />
                          <div className="relative flex min-h-0 flex-1 flex-row overflow-hidden">
                            <WorkspaceLeftRail />

                            <div className="flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden">
                              <OsFileDropLayer
                                className={cn(
                                  "relative min-h-0 min-w-0 flex-1 overflow-hidden",
                                  // The home surface sits on the board world's
                                  // ground: the shell's n-05 + the DS line grid.
                                  isGroundedSurface && "page-grid-ground",
                                )}
                                enabled={isDropZoneEnabled}
                              >
                                <WorkspaceMainRegion
                                  mainRef={mainRef}
                                  isFullViewport={!!isFullViewport}
                                  isGroundedSurface={isGroundedSurface}
                                >
                                  {children}
                                </WorkspaceMainRegion>

                                {/* While the pane is expanded full-width, the
                                  region behind the dock + pane pair is dead
                                  space that flashes live canvas whenever their
                                  measured widths disagree for a frame (rail
                                  drags, dock drags). Paint it pane-colored so
                                  any mismatch shows surface, not canvas. Below
                                  the chat shells (z-30/z-50), above content. */}
                                {isRightPanelOpen && isRightPanelExpanded && (
                                  <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 z-20 bg-surface-rail"
                                  />
                                )}
                                {/* FAB / floating window, absolutely positioned
                                  over the content region (it renders nothing
                                  off the home/workspace-detail routes). */}
                                <WorkspaceFloatingChatPanel />
                              </OsFileDropLayer>

                              <RightPaneRouter
                                maxPaneWidth={rightPaneMaxWidth}
                                isRightPanelOpen={isRightPanelOpen}
                                onRightPanelWidthChange={setRightPanelWidth}
                                setIsExpanded={setIsRightPanelExpanded}
                                isExpanded={isRightPanelExpanded}
                                leftChatDockPx={leftChatDockPx}
                                leftRailWidth={leftRailWidth}
                              />
                            </div>
                          </div>
                        </CanvasDndConditional>
                      </CanvasSelectionMirrorProvider>
                    </CanvasCommentsMirrorProvider>
                  </CanvasNodesMirrorProvider>
                </CanvasClipboardProvider>
              </WorkspaceFloatingChatProvider>
            </RightPaneExpandContext.Provider>
          </div>
        </>
      </FileConflictResolverProvider>
    </LayoutSettingsShell>
  );
};

export default React.memo(withSidebarContext(WorkspaceLayout));
